import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSearch,
  faStar,
  faReply,
  faCircleExclamation,
  faCircleCheck,
  faComments,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const HerKartFeedback = () => {
  const { fetchMyFeedback, replyToFeedback } = useHerKart();

  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("All");

  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);

  const load = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await fetchMyFeedback();
      if (response?.success) {
        setFeedbackList(response.feedback || []);
      }
    } catch (e) {
      console.error("Feedback fetch error:", e);
      setMessage({ type: "error", text: "Could not load feedback." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredFeedback = useMemo(() => {
    let list = [...feedbackList];

    if (levelFilter !== "All") {
      list = list.filter((f) => f.level === levelFilter.toLowerCase());
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((f) =>
        [f.userName, f.refName, f.message].join(" ").toLowerCase().includes(keyword)
      );
    }

    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [feedbackList, search, levelFilter]);

  const openReply = (feedback) => {
    setReplyingId(feedback._id);
    setReplyText(feedback.sellerReply || "");
    setMessage(null);
  };

  const closeReply = () => {
    setReplyingId(null);
    setReplyText("");
  };

  const submitReply = async (id) => {
    setSubmittingReply(true);
    try {
      const response = await replyToFeedback(id, replyText.trim());
      if (response?.success) {
        setFeedbackList((prev) => prev.map((f) => (f._id === id ? response.feedback : f)));
        setMessage({ type: "success", text: "Reply sent." });
        closeReply();
      } else {
        setMessage({ type: "error", text: "Failed to send reply." });
      }
    } catch (e) {
      console.error("Reply error:", e);
      setMessage({ type: "error", text: "Failed to send reply." });
    } finally {
      setSubmittingReply(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/dashboard"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-pink-300">Feedback</h1>
              <p className="text-xs text-slate-200">See what customers are saying</p>
            </div>
          </div>

          <div className="rounded-xl bg-pink-600/20 px-4 py-2 text-pink-300">
            <span className="font-semibold">{filteredFeedback.length}</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        {message && (
          <div
            className={`mb-4 flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon icon={message.type === "success" ? faCircleCheck : faCircleExclamation} />
            {message.text}
          </div>
        )}

        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by customer, product/store, or message..."
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 outline-none focus:border-pink-500"
              />
            </div>

            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
            >
              <option>All</option>
              <option>Store</option>
              <option>Product</option>
              <option>Service</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-200">Loading feedback...</div>
        ) : filteredFeedback.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
            <FontAwesomeIcon icon={faComments} className="mx-auto mb-4 text-4xl text-slate-300" />
            <h3 className="text-xl font-semibold">No feedback yet</h3>
            <p className="mt-2 text-sm text-gray-200">
              {feedbackList.length === 0
                ? "Customer feedback will appear here once received."
                : "No feedback matches your search or filter."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFeedback.map((fb) => (
              <div key={fb._id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-yellow-100 font-semibold">{fb.userName}</h3>
                    <span className="rounded-full bg-violet-600/20 px-2 py-0.5 text-xs font-semibold text-violet-300 capitalize">
                      {fb.level}
                    </span>
                    {fb.refName && <span className="text-sm text-gray-200">on {fb.refName}</span>}
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(fb.createdAt).toLocaleDateString()}
                  </span>
                </div>

                {fb.rating > 0 && (
                  <div className="mt-2 flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FontAwesomeIcon
                        key={star}
                        icon={faStar}
                        className={fb.rating >= star ? "text-amber-400" : "text-slate-700"}
                      />
                    ))}
                  </div>
                )}

                {fb.message && <p className="mt-2 text-sm text-gray-200">{fb.message}</p>}

                {fb.sellerReply ? (
                  <div className="mt-3 rounded-xl border border-pink-500/20 bg-pink-500/5 p-3">
                    <p className="text-xs font-semibold text-pink-300">Your Reply</p>
                    <p className="mt-1 text-sm text-gray-200">{fb.sellerReply}</p>
                  </div>
                ) : replyingId === fb._id ? (
                  <div className="mt-3">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value.slice(0, 250))}
                      style={{height: "100px"}}
                      maxLength={250}
                      placeholder="Write a reply..."
                      className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm outline-none focus:border-pink-500"
                    />
                    <div className="mt-1 text-right text-xs text-slate-500">{replyText.length}/250</div>
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={() => submitReply(fb._id)}
                        disabled={submittingReply}
                        className="rounded-xl bg-pink-600 w-fit px-2 py-1 text-xs font-semibold hover:bg-pink-500 disabled:opacity-60"
                      >
                        {submittingReply ? "Sending..." : "Send Reply"}
                      </button>
                      <button
                        onClick={closeReply}
                        className="rounded-xl border border-white/10 w-fit px-2 py-1 text-xs hover:bg-white/10"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openReply(fb)}
                    className="mt-3 flex w-fit px-2 py-1 items-center gap-2 rounded-xl border border-white/10 bg-white/5 text-xs hover:bg-white/10"
                  >
                    <FontAwesomeIcon icon={faReply} />
                    Reply
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HerKartFeedback;